import React, { useMemo } from 'react';

import PropTypes from 'prop-types';

import { cn } from '@/lib/utils';
import { DAYS } from '../../../constants';
import { useCalendar, useFilters } from '../../../hooks';
import {
  filterAppointments,
  getAppointmentsByDate,
  getMonthDates,
  isInSameMonth,
  isToday,
} from '../../../utils';
import DateCell from './DateCell';
import HeaderCell from './HeaderCell';

const CALENDAR_GRID_STYLES = cn(
  'grid grid-cols-7 flex-1 min-h-0',
  '*:border-r *:border-b',
  '[&>:nth-child(7n)]:border-r-0',
  '[&>:nth-last-child(-n+7)]:border-b-0'
);

// The number of weeks per month (i.e calendar rows) will dictate the size of each
// <DateCell /> and the number of appointments that can be shown in those cells. Show a
// maximum of 6 appointments per day in a 4-week month (4 rows), 4 in a 5-week month (5
// rows), and 3 in a 6-week month (6 rows).
const MAX_VISIBLE_APPOINTMENTS_BY_WEEK_COUNT = { 4: 6, 5: 4, 6: 3 };

const EmptyCell = () => <div className="bg-muted/60" />;

const Month = ({ currentDate }) => {
  const { appointments } = useCalendar();

  const {
    activeFilters: { creators: creatorIds, tags: tagIds },
  } = useFilters();

  const filteredAppointments = useMemo(
    () => filterAppointments({ appointments, creatorIds, tagIds }),
    [appointments, creatorIds, tagIds]
  );

  const monthDates = useMemo(() => getMonthDates(currentDate), [currentDate]);

  const appointmentsByDate = useMemo(
    () =>
      getAppointmentsByDate({ appointments: filteredAppointments, dates: monthDates }),
    [filteredAppointments, monthDates]
  );

  const weekCount = monthDates.length / 7;
  const maxVisibleAppointments = MAX_VISIBLE_APPOINTMENTS_BY_WEEK_COUNT[weekCount] ?? 3;

  return (
    <div className="flex flex-col flex-1 min-h-0">
      <div className="grid grid-cols-7 border-b">
        {DAYS.map((dayName) => (
          <HeaderCell dayName={dayName} key={dayName} />
        ))}
      </div>

      <div
        className={CALENDAR_GRID_STYLES}
        style={{ gridTemplateRows: `repeat(${weekCount}, minmax(0, 1fr))` }}
      >
        {monthDates.map((date) =>
          isInSameMonth(date, currentDate) ? (
            <DateCell
              appointments={appointmentsByDate[date.toDateString()]}
              dayNumber={date.getDate()}
              isToday={isToday(date)}
              key={date.toISOString()}
              maxVisibleAppointments={maxVisibleAppointments}
            />
          ) : (
            <EmptyCell key={date.toISOString()} />
          )
        )}
      </div>
    </div>
  );
};

Month.propTypes = {
  currentDate: PropTypes.instanceOf(Date).isRequired,
};

export default Month;
